// typescript
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  Input,
  ChangeDetectionStrategy,
  TemplateRef,
  ElementRef,
  AfterViewInit,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators, FormArray, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { debounceTime, map } from 'rxjs/operators';
import {
  TableConfig,
  TableComponent,
  AlertType,
  Alert,
  AlertService,
  PaginatorFetcher,
  ModalComponent,
  ModalResult
} from '@michelin/theme';
import { ProductDataService } from '../../services/product-data.service';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { Store } from '@ngxs/store';
import { CasingGradeSpecification } from '../../models/casing-grade-specification.model';
import { distinctUntilChanged, Observable, of } from 'rxjs';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { CasingGradeConstants } from '../../models/casing-grade.constants';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';

@Component({
  selector: 'app-casing-grade-table-details',
  templateUrl: './casing-grade-table-details.component.html',
  styleUrls: ['./casing-grade-table-details.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingGradeTableDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingGradeSpecification>;
  @ViewChild('deleteConfirmation', { static: false }) deleteConfirmation: ModalComponent;
  @ViewChild('deleteContent', { static: false }) deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: false }) discardConfirmation: ModalComponent;
  @ViewChild('discardContent', { static: false }) discardContent: TemplateRef<any>;
  @ViewChild('casingGradeTableCodeInput', { static: false }) casingGradeTableCodeInput: ElementRef;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  public route = inject(ActivatedRoute);
  public router = inject(Router);

  @Input() casingGradeTablePk: string;
  casingGradeTableCode: string;
  casingGradeTableDesc: string;
  filterFormBackend: FormGroup;
  casingGradeSpecList: CasingGradeSpecification[] = [];
  defaultCasingGradeSpecs: CasingGradeSpecification[] = [];
  specificCasingGradeSpecs: CasingGradeSpecification[] = [];
  locallyAddedCasingSpec: CasingGradeSpecification[] = [];
  nonDeletableGrades: string[] = [];
  filterForm: FormGroup;
  productSaveDisabled = true;
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  discardMessage = '';
  private itemToDelete: CasingGradeSpecification | null = null;
  mode: 'create' | 'copy' | 'edit' = 'edit';
  sourceCasingGradeTablePk: string;
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  defaultCasingGrade: string[] = [];
  axleOptions: string[] = [];
  private originalSpecificSpecs: CasingGradeSpecification[] = [];
  private originalDefaultSpec: any = {};
  isSaving = false;
  isDeleting = false;
  isSaved = false;

  // NEW: trackers for unsaved modifications
  private modifiedSpecificRows: Record<string, Partial<CasingGradeSpecification>> = {};
  private defaultModifiedValues: any = null;
  // NEW: track removed specs in copy mode so modification detection works
  private removedSpecificSpecIds: string[] = [];

  tableConfig: TableConfig<CasingGradeSpecification> = {
    paginator: {
      type: 'scroll',
      pageSize: 20
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<CasingGradeSpecification> = () => {
    this.generateFilterFormBackend();
    if (this.mode !== 'create') {
      // Capture unsaved changes before backend fetch
      this.captureUnsavedModifications();
    }
    if (this.mode === 'create') {
      return this.handleCreateModeFetch();
    }
    return this.handleEditOrCopyModeFetch();
  };

  private handleCreateModeFetch(): Observable<{ items: CasingGradeSpecification[]; count: number }> {
    return of({ items: [...this.locallyAddedCasingSpec], count: this.locallyAddedCasingSpec.length });
  }

  private handleEditOrCopyModeFetch(): Observable<{ items: CasingGradeSpecification[]; count: number }> {
    const pkToUse = this.mode === 'copy' ? this.sourceCasingGradeTablePk : this.casingGradeTablePk;
    return this.productDataService
      .getAllCasingGradeSpecification(pkToUse, this.currentPage, this.pageSize)
      .pipe(map((response) => this.processSpecificationResponse(response)));
  }

  private processSpecificationResponse(response: any): { items: CasingGradeSpecification[]; count: number } {
    this.initializeSpecificationLists(response);
    this.separateSpecificationTypes();
    this.handleDefaultSpecification();
    this.applySpecificRowModifications(); // reapply unsaved row edits
    const combined = [...this.locallyAddedCasingSpec, ...this.specificCasingGradeSpecs];
    this.updateTableWithCombinedData(combined);
    return { items: combined, count: combined.length };
  }

  private initializeSpecificationLists(response: any): void {
    const specs = response.data.casingGradeSpecifications || [];
    this.casingGradeSpecList = Array.isArray(specs) ? specs : [];
    // NEW: exclude previously locally deleted specs when in copy mode so they don't reappear after backend fetch
    if (this.mode === 'copy' && this.removedSpecificSpecIds.length > 0) {
      this.casingGradeSpecList = this.casingGradeSpecList.filter((spec) => !this.removedSpecificSpecIds.includes(spec.casingGradeSpecPk));
    }
  }

  private separateSpecificationTypes(): void {
    this.defaultCasingGradeSpecs = this.casingGradeSpecList.filter((spec) => spec.casingGradeSpecType === 'DEFAULT');
    this.specificCasingGradeSpecs = this.casingGradeSpecList.filter((spec) => spec.casingGradeSpecType === 'SPECIFIC');

    // Only overwrite originals if there are no unsaved modifications AND no deletions
    if (Object.keys(this.modifiedSpecificRows).length === 0 && this.removedSpecificSpecIds.length === 0) {
      this.originalSpecificSpecs = structuredClone(this.specificCasingGradeSpecs);
    }
  }

  private handleDefaultSpecification(): void {
    if (this.defaultCasingGradeSpecs.length === 0) return;
    const defaultSpec = this.defaultCasingGradeSpecs[0];
    const extracted = this.extractDefaultSpecValues(defaultSpec);

    // If user has unsaved modifications, keep form values & do not reset originals
    const defaultWasModified = !!this.defaultModifiedValues;
    if (!defaultWasModified) {
      this.originalDefaultSpec = extracted;
      this.filterForm.patchValue(extracted, { emitEvent: false });
    } else {
      // Merge unsaved modified values into backend spec object (so future save payload is consistent)
      Object.assign(defaultSpec, {
        casingGrade: this.defaultModifiedValues.defaultCasingGrade,
        rmCasingPrefix: this.defaultModifiedValues.rmCasingPrefix,
        rmCasingSuffix: this.defaultModifiedValues.rmCasingSuffix,
        fgCasingSuffix: this.defaultModifiedValues.fgCasingSuffix,
        fgCapCasingPrefix: this.defaultModifiedValues.fgCapPrefix,
        fgCasingPrefix: this.defaultModifiedValues.fgCasingPrefix,
        fgCapCasingSuffix: this.defaultModifiedValues.fgCapSuffix
      });
      // Do NOT change originalDefaultSpec so modification detection still works
    }
  }

  private extractDefaultSpecValues(spec: CasingGradeSpecification): any {
    return {
      defaultCasingGrade: spec.casingGrade || '',
      rmCasingPrefix: spec.rmCasingPrefix || '',
      rmCasingSuffix: spec.rmCasingSuffix || '',
      fgCasingSuffix: spec.fgCasingSuffix || '',
      fgCapPrefix: spec.fgCapCasingPrefix || '',
      fgCasingPrefix: spec.fgCasingPrefix || '',
      fgCapSuffix: spec.fgCapCasingSuffix || ''
    };
  }

  private updateTableWithCombinedData(combined: CasingGradeSpecification[]): void {
    this.getTableRules(combined);
    this.updateDisplayedRowsForm(combined);
    this.cdr.markForCheck();
  }

  constructor() {
    this.filterForm = this.fb.group({
      casingGradeTableCode: [''],
      casingGradeTableDesc: [''],
      defaultCasingGrade: ['', Validators.required],
      rmCasingPrefix: [''],
      rmCasingSuffix: [''],
      fgCasingSuffix: [''],
      fgCapPrefix: [''],
      fgCasingPrefix: [''],
      fgCapSuffix: ['']
    });
  }

  ngOnInit(): void {
    const routePath = this.getRoutePath();
    switch (routePath) {
      case 'create':
        this.setupCreateMode();
        break;
      case 'copy':
        this.setupCopyMode();
        break;
      default:
        this.setupEditMode();
        break;
    }

    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.showReadOnlyAlert();
    }

    this.configureFormControls();
    this.patchFormValues();
    this.updateCodeDescValidatorsForMode();
    this.setupTableColumns();
    this.initializeComponentData();
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private getRoutePath(): string {
    const urlSegments = this.route?.snapshot?.url ?? [];
    return urlSegments.length > 0 ? urlSegments[urlSegments.length - 1]?.path : this.route?.snapshot?.routeConfig?.path ?? '';
  }

  private setupCreateMode(): void {
    this.mode = 'create';
    this.casingGradeTablePk = null;
    this.initializeCreateMode();
  }

  private setupCopyMode(): void {
    this.mode = 'copy';
    this.sourceCasingGradeTablePk = this.route.snapshot.paramMap.get('casingGradeTablePk');
    this.casingGradeTableCode = '';
    this.casingGradeTableDesc = '';
  }

  private setupEditMode(): void {
    this.mode = 'edit';
    this.casingGradeTablePk = this.route.snapshot.paramMap.get('casingGradeTablePk');
    const { code, description } = history.state || {};
    this.casingGradeTableCode = code || '';
    this.casingGradeTableDesc = description || '';
  }

  private showReadOnlyAlert(): void {
    this.alertService.showAlert({
      type: AlertType.classical,
      title: this.translationService.translate('readOnlyProductDataHeader'),
      content: this.translationService.translate('readOnlyProductDataText'),
      timestamp: 6000
    } as Alert);
  }

  private configureFormControls(): void {
    if (!this.canEditCasingGrade) {
      this.disableAllFormControls();
    } else if (this.mode === 'create' || this.mode === 'copy') {
      this.filterForm.get('casingGradeTableCode')?.enable({ emitEvent: false });
      this.filterForm.get('casingGradeTableDesc')?.enable({ emitEvent: false });
    } else if (this.mode === 'edit') {
      // Ensure controls are disabled (read-only) so validators & errors don't trigger UI feedback
      this.filterForm.get('casingGradeTableCode')?.disable({ emitEvent: false });
      this.filterForm.get('casingGradeTableDesc')?.disable({ emitEvent: false });
    }
  }

  private patchFormValues(): void {
    this.filterForm.patchValue({
      casingGradeTableCode: this.casingGradeTableCode,
      casingGradeTableDesc: this.casingGradeTableDesc
    });
  }

  private setupTableColumns(): void {
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = this.getTableColumns();
      this.cdr.markForCheck();
    });
  }

  private getTableColumns(): any {
    return {
      makeTier: { header: this.translationService.translate('MakeTireText'), cell: 'makeTier' },
      ntr: { header: this.translationService.translate('ntrText'), cell: 'ntr' },
      ageMonths: { header: this.translationService.translate('ageMonthsText'), cell: 'ageMonths' },
      axle: { header: this.translationService.translate('axleText'), cell: 'axle' },
      sizeTier: { header: this.translationService.translate('sizeTireText'), cell: 'sizeTier' },
      nail: { header: this.translationService.translate('nailText'), cell: 'nail' },
      rnailCrown: { header: this.translationService.translate('rnailCrownText'), cell: 'rnailCrown' },
      rnailShoulder: { header: this.translationService.translate('rnailShoulderText'), cell: 'rnailShoulder' },
      sr: { header: this.translationService.translate('srText'), cell: 'sr' },
      max: { header: this.translationService.translate('maxText'), cell: 'max' },
      grade: { header: this.translationService.translate('gradeText'), cell: 'grade' },
      rmCasingPrefix: { header: this.translationService.translate('rmCasingPrefixText'), cell: 'rmCasingPrefix' },
      rmCasingSuffix: { header: this.translationService.translate('rmCasingSuffixText'), cell: 'rmCasingSuffix' },
      fgCasingPrefix: { header: this.translationService.translate('fgCasingPrefixText'), cell: 'fgCasingPrefix' },
      fgCasingSuffix: { header: this.translationService.translate('fgCasingSuffixText'), cell: 'fgCasingSuffix' },
      fgCapCasingPrefix: { header: this.translationService.translate('fgCapPrefixText'), cell: 'fgCapCasingPrefix' },
      fgCapCasingSuffix: { header: this.translationService.translate('fgCapSuffixText'), cell: 'fgCapCasingSuffix' },
      action: { cell: 'action', header: this.translationService.translate('actionText') }
    };
  }

  private initializeComponentData(): void {
    this.axleOptions = ['All', ...this.store.selectSnapshot(ProductsState.products).axles];
    this.defaultCasingGrade = this.store.selectSnapshot(ProductsState.products).casingGrade;
    this.initializeForm();
    this.setupFormListeners();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  ngAfterViewInit(): void {
    if ((this.mode === 'create' || this.mode === 'copy') && this.casingGradeTableCodeInput) {
      setTimeout(() => this.casingGradeTableCodeInput.nativeElement.focus(), 100);
    }
  }

  private initializeForm(): void {
    this.updateDisplayedRowsForm(this.casingGradeSpecList);
  }

  private initializeCreateMode(): void {
    this.resetCasingGradeTableDetails();
    this.resetFormValues();
    this.updateCodeDescValidatorsForMode();
    this.storeEmptyOriginalValues();
    this.updateDisplayedRowsForm([]);
    this.cdr.markForCheck();
  }

  private resetCasingGradeTableDetails(): void {
    this.casingGradeTableCode = '';
    this.casingGradeTableDesc = '';
    this.casingGradeSpecList = [];
    this.defaultCasingGradeSpecs = [];
    this.specificCasingGradeSpecs = [];
    this.locallyAddedCasingSpec = [];
  }

  private resetFormValues(): void {
    this.filterForm.patchValue(
      {
        casingGradeTableCode: '',
        casingGradeTableDesc: '',
        defaultCasingGrade: '',
        rmCasingPrefix: '',
        rmCasingSuffix: '',
        fgCasingSuffix: '',
        fgCapPrefix: '',
        fgCasingPrefix: '',
        fgCapSuffix: ''
      },
      { emitEvent: false }
    );
  }

  private storeEmptyOriginalValues(): void {
    this.originalDefaultSpec = {
      defaultCasingGrade: '',
      rmCasingPrefix: '',
      rmCasingSuffix: '',
      fgCasingSuffix: '',
      fgCapPrefix: '',
      fgCasingPrefix: '',
      fgCapSuffix: ''
    };
    this.originalSpecificSpecs = [];
  }

  private updateCodeDescValidatorsForMode(): void {
    const codeControl = this.filterForm.get('casingGradeTableCode');
    const descControl = this.filterForm.get('casingGradeTableDesc');
    if (this.mode === 'create' || this.mode === 'copy') {
      codeControl?.setValidators([Validators.required]);
      descControl?.setValidators([Validators.required]);
    } else {
      // Edit mode: remove validators & clear any lingering errors
      codeControl?.clearValidators();
      descControl?.clearValidators();
      codeControl?.setErrors(null);
      descControl?.setErrors(null);
      codeControl?.markAsPristine();
      codeControl?.markAsUntouched();
      descControl?.markAsPristine();
      descControl?.markAsUntouched();
    }
    codeControl?.updateValueAndValidity({ emitEvent: false });
    descControl?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  createRowForm(row: CasingGradeSpecification): FormGroup {
    const fg = this.fb.group({
      casingGradeTier: [row.casingGradeTier, [Validators.required, Validators.min(0)]],
      casingGradeNtr: [row.casingGradeNtr, [Validators.required, Validators.min(0)]],
      casingGradeAge: [row.casingGradeAge, [Validators.required, Validators.min(0)]],
      casingGradeAxle: [row.casingGradeAxle, [Validators.required]],
      casingGradeCasingSize: [row.casingGradeCasingSize, [Validators.required, Validators.min(0)]],
      casingGradeNail: [row.casingGradeNail, [Validators.required, Validators.min(0)]],
      casingGradeRnailCrown: [row.casingGradeRnailCrown, [Validators.required, Validators.min(0)]],
      casingGradeRnailShoulder: [row.casingGradeRnailShoulder, [Validators.required, Validators.min(0)]],
      casingGradeSr: [row.casingGradeSr, [Validators.required, Validators.min(0)]],
      casingGradeMaxRepairs: [row.casingGradeMaxRepairs, [Validators.min(0)]],
      casingGrade: [row.casingGrade, [Validators.required]],
      rmCasingPrefix: [row.rmCasingPrefix],
      rmCasingSuffix: [row.rmCasingSuffix],
      fgCasingPrefix: [row.fgCasingPrefix],
      fgCasingSuffix: [row.fgCasingSuffix],
      fgCapCasingPrefix: [row.fgCapCasingPrefix],
      fgCapCasingSuffix: [row.fgCapCasingSuffix]
    });
    if (!this.canEditCasingGrade) fg.disable();
    fg.valueChanges.subscribe((values) => Object.assign(row, values));
    return fg;
  }

  private updateDisplayedRowsForm(rows: CasingGradeSpecification[]): void {
    this.syncFormToDataModel();
    const formArray = this.fb.array(rows.map((r) => this.createRowForm(r)));
    this.displayedRowsForm.setControl('rows', formArray);
    this.cdr.detectChanges();
  }

  private validateRowsHaveGrade(): boolean {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray || formArray.length === 0) return true;
    let all = true;
    formArray.controls.forEach((fg) => {
      const grade = (fg as FormGroup).get('casingGrade') as FormControl;
      if (!grade?.value) {
        grade.markAsTouched();
        grade.setErrors({ required: true });
        all = false;
      }
    });
    this.cdr.markForCheck();
    return all;
  }

  private validateRowsHaveAxle(): boolean {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray?.length) return true;
    let all = true;
    formArray.controls.forEach((fg) => {
      const axle = (fg as FormGroup).get('casingGradeAxle') as FormControl;
      if (!axle?.value) {
        axle.markAsTouched();
        axle.setErrors({ required: true });
        all = false;
      }
    });
    this.cdr.markForCheck();
    return all;
  }

  private validateDefaultCasingGrade(): boolean {
    const ctrl = this.filterForm.get('defaultCasingGrade');
    const formVal = ctrl?.value;
    const defaultSpecGrade = this.defaultCasingGradeSpecs?.[0]?.casingGrade || '';
    if (!defaultSpecGrade && !formVal) {
      ctrl?.markAsTouched();
      ctrl?.setErrors({ required: true });
      this.cdr.markForCheck();
      const alertContent = this.translationService.translate('gradeMandatoryText') || 'Default Casing Grade is mandatory for all rows';
      this.alertService.showAlert({ type: AlertType.strong, content: alertContent } as Alert);
      return false;
    }
    return true;
  }

  getControl(item: CasingGradeSpecification, controlName: keyof CasingGradeSpecification): FormControl {
    const index = [...this.locallyAddedCasingSpec, ...this.specificCasingGradeSpecs].findIndex(
      (g) => g.casingGradeSpecPk === item.casingGradeSpecPk
    );
    if (index === -1) return new FormControl('');
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    return (formArray.at(index) as FormGroup)?.get(controlName) as FormControl;
  }

  getTableRules(casingGradeSpec: CasingGradeSpecification[]): void {
    const payload = casingGradeSpec.map((g) => g.casingGradeSpecPk);
    this.productDataService.checkGradeTableRules(payload).subscribe((response: any) => {
      this.nonDeletableGrades = Array.isArray(response?.data) ? response.data : [];
    });
  }

  async onAddNewRule(): Promise<void> {
    this.syncFormToDataModel();
    this.productDataService.getNewCasingGradePk().subscribe({
      next: (response: any) => {
        const newId = response?.data;
        const newRow: CasingGradeSpecification = {
          casingGradeSpecPk: newId,
          casingGradeTier: '',
          casingGradeNtr: '',
          casingGradeAge: '',
          casingGradeCasingSize: '',
          casingGradeAxle: this.axleOptions[0] || '',
          casingGradeNail: '',
          casingGradeRnailCrown: '',
          casingGradeRnailShoulder: '',
          casingGradeSr: '',
          casingGradeMaxRepairs: '',
          casingGrade: '',
          rmCasingPrefix: '',
          rmCasingSuffix: '',
          fgCasingPrefix: '',
          fgCasingSuffix: '',
          fgCapCasingPrefix: '',
          fgCapCasingSuffix: '',
          casingGradeFk: '',
          casingTableFk: '',
          casingGradeSpecType: 'SPECIFIC',
          dealerFk: ''
        };
        this.locallyAddedCasingSpec.unshift(newRow);
        const formArray = this.displayedRowsForm.get('rows') as FormArray;
        formArray.insert(0, this.createRowForm(newRow));
        this.cdr.detectChanges();
        // Trigger table refresh without losing unsaved edits (preserved by captureUnsavedModifications in fetcher)
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  getPrefixSuffixList(): string[] {
    return CasingGradeConstants.CASING_GRADE_VALUES;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.onDeleteCasingGradeSpecification(this.itemToDelete!);
      }
      this.itemToDelete = null;
    }
  }

  showDeleteModal(item: CasingGradeSpecification): void {
    this.deleteConfirmation.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item;
  }

  onDeleteCasingGradeSpecification(item: CasingGradeSpecification): void {
    if (!this.canEditCasingGrade) return;
    this.syncFormToDataModel();

    // NEW: In copy mode, only delete locally; skip backend API call
    if (this.mode === 'copy') {
      const localIndex = this.locallyAddedCasingSpec.findIndex((spec) => spec.casingGradeSpecPk === item.casingGradeSpecPk);
      if (localIndex !== -1) {
        this.locallyAddedCasingSpec.splice(localIndex, 1);
      } else {
        // Remove from specific list and record removal for modification tracking
        this.specificCasingGradeSpecs = this.specificCasingGradeSpecs.filter((spec) => spec.casingGradeSpecPk !== item.casingGradeSpecPk);
        if (item.casingGradeSpecPk) {
          // Ensure we only record once
          if (!this.removedSpecificSpecIds.includes(item.casingGradeSpecPk)) {
            this.removedSpecificSpecIds.push(item.casingGradeSpecPk);
          }
        }
      }
      this.refreshFormAndTable();
      this.showSuccessAlert('deleteSuccessText', 3000);
      this.itemToDelete = null;
      return;
    }

    const localIndex = this.locallyAddedCasingSpec.findIndex((spec) => spec.casingGradeSpecPk === item.casingGradeSpecPk);
    if (localIndex !== -1 || !item.casingGradeSpecPk) {
      if (localIndex !== -1) this.locallyAddedCasingSpec.splice(localIndex, 1);
      this.refreshFormAndTable();
      this.showSuccessAlert('deleteSuccessText', 3000);
    } else {
      this.isDeleting = true;
      this.cdr.markForCheck();
      this.productDataService.deleteCasingGradeSpecification(item.casingGradeSpecPk).subscribe({
        next: () => {
          this.isDeleting = false;
          this.removePersistedItem(item);
          this.refreshFormAndTable();
          this.showSuccessAlert('deleteSuccessText', 3000);
        },
        error: (error) => {
          this.isDeleting = false;
          this.showErrorAlert(error?.error?.message || 'errorOccurred');
        }
      });
    }
    this.itemToDelete = null;
  }

  private refreshFormAndTable(): void {
    const combined = [...this.locallyAddedCasingSpec, ...this.specificCasingGradeSpecs];
    this.updateDisplayedRowsForm(combined);
    this.tableRef.resetFilters();
    this.generateFilterFormBackend();
    this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    this.cdr.detectChanges();
  }

  private removePersistedItem(item: CasingGradeSpecification): void {
    this.specificCasingGradeSpecs = this.specificCasingGradeSpecs.filter((s) => s.casingGradeSpecPk !== item.casingGradeSpecPk);
    this.originalSpecificSpecs = this.originalSpecificSpecs.filter((s) => s.casingGradeSpecPk !== item.casingGradeSpecPk);
  }

  private showSuccessAlert(key: string, duration: number): void {
    this.alertService.showAlert({
      type: AlertType.success,
      content: this.translationService.translate(key),
      timestamp: duration
    } as Alert);
  }

  private showErrorAlert(message: string): void {
    this.alertService.showAlert({
      type: AlertType.critical,
      content: this.translationService.translate(message),
      timestamp: 5000
    } as Alert);
  }

  showDiscardModal(): void {
    this.discardMessage = this.buildDiscardMessage();
    this.discardConfirmation.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result?.id === 'discardConfirmation' && result.result) {
      this.resetUnsavedState();
      this.router.navigate(['/home/productData/casing-grade-table/']);
    }
  }

  private buildDiscardMessage(): string {
    const translated = this.translationService.translate('unSaveChangesText');
    return translated !== 'unSaveChangesText' ? translated : 'Are you sure you want to leave this page with unsaved changes?';
  }

  public resetUnsavedState(): void {
    try {
      this.filterForm.patchValue(this.originalDefaultSpec || {}, { emitEvent: false });
      this.locallyAddedCasingSpec = [];
      this.specificCasingGradeSpecs = [...(this.originalSpecificSpecs || [])];
      this.updateDisplayedRowsForm([...this.locallyAddedCasingSpec, ...this.specificCasingGradeSpecs]);
      this.defaultModifiedValues = null;
      this.modifiedSpecificRows = {};
      // NEW: clear removed specs tracker
      this.removedSpecificSpecIds = [];
      this.filterForm.markAsPristine();
      this.displayedRowsForm.markAsPristine();
    } catch (e) {
      console.error('Error resetting unsaved state:', e);
    }
    this.cdr.markForCheck();
  }

  canDeactivate(): Promise<boolean> {
    if (this.productSaveDisabled) {
      return Promise.resolve(true);
    }
    if (this.checkIfModified() && !this.isSaved) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const sub = this.discardConfirmation.result.subscribe((modalResult: ModalResult) => {
          const proceed = modalResult?.id === 'discardConfirmation' && !!modalResult.result;
          if (proceed) this.resetUnsavedState();
          resolve(proceed);
          sub.unsubscribe();
        });
      });
    }
    return Promise.resolve(true);
  }

  onSave(): void {
    if (this.isSaving) return;
    // Validate code (always) and description (create/copy) before proceeding
    if (this.isCodeOrDescInvalid()) return;
    if (this.isFormInvalid()) return;
    if (this.isEditModeWithoutChanges()) return;
    this.syncFormToDataModel();
    if (!this.validateRowsHaveGrade()) {
      this.showValidationAlert('gradeMandatoryText', 'Grade is mandatory for all rows');
      return;
    }
    if (!this.validateRowsHaveAxle()) {
      this.showValidationAlert('axleMandatoryText', 'Axle is mandatory for all rows');
      return;
    }
    if (!this.validateDefaultCasingGrade()) return;

    this.isSaving = true;
    this.cdr.markForCheck();
    if (this.mode === 'create' || this.mode === 'copy') {
      this.createCasingGradeTable();
    } else {
      this.updateCasingGradeTable();
    }
  }

  private isFormInvalid(): boolean {
    if (this.filterForm.invalid) {
      this.filterForm.markAllAsTouched();
      this.showAlert('defaultGradeMandatoryText');
      return true;
    }
    return false;
  }

  private isCodeOrDescInvalid(): boolean {
    const codeControl = this.filterForm.get('casingGradeTableCode');
    const descControl = this.filterForm.get('casingGradeTableDesc');
    codeControl?.markAsTouched();
    // Description only touched when create/copy
    if (this.mode === 'create' || this.mode === 'copy') descControl?.markAsTouched();
    const missing: string[] = [];
    if (codeControl?.invalid) missing.push('Code');
    if (descControl?.invalid) missing.push('Description');
    if (missing.length) {
      this.showMandatoryFields(missing);
      return true;
    }
    return false;
  }

  private isEditModeWithoutChanges(): boolean {
    if (!this.checkIfModified() && this.mode === 'edit') {
      this.showAlert('noModifiedText');
      return true;
    }
    return false;
  }

  private showAlert(key: string, fallback?: string): void {
    const translated = this.translationService.translate(key);
    const content = translated && translated !== key ? translated : fallback || key;
    this.alertService.showAlert({ type: AlertType.strong, content } as Alert);
  }

  private showValidationAlert(key: string, fallback: string): void {
    const translated = this.translationService.translate(key);
    const content = translated && translated !== key ? translated : fallback;
    this.alertService.showAlert({ type: AlertType.strong, content } as Alert);
  }

  private showMandatoryFields(missing: string[]): void {
    if (!missing || missing.length === 0) return;
    const baseKey = 'mandatoryMissingText';
    const baseTranslation = this.translationService.translate(baseKey);
    const baseText = baseTranslation !== baseKey ? baseTranslation : 'The following fields are mandatory: ';

    // Attempt per-field translation key pattern mandatoryMissingText<FieldName>
    const labels = missing.map((field) => {
      const key = baseKey + field; // e.g., mandatoryMissingTextCode
      const translated = this.translationService.translate(key);
      return translated !== key ? translated : field;
    });

    const message = baseText + labels.join(', ');
    this.alertService.showAlert({ type: AlertType.strong, content: message } as Alert);
  }

  private createCasingGradeTable(): void {
    if (this.defaultCasingGradeSpecs.length === 0) {
      this.defaultCasingGradeSpecs = [
        {
          casingGradeSpecPk: null,
          dealerFk: null,
          casingGradeTier: '',
          casingGradeNtr: '',
          casingGradeAge: '',
          casingGradeAxle: '',
          casingGradeCasingSize: '',
          casingGradeNail: '',
          casingGradeRnailCrown: '',
          casingGradeRnailShoulder: '',
          casingGradeSr: '',
          casingGradeMaxRepairs: '',
          casingGrade: this.filterForm.get('defaultCasingGrade')?.value,
          rmCasingPrefix: this.filterForm.get('rmCasingPrefix')?.value,
          rmCasingSuffix: this.filterForm.get('rmCasingSuffix')?.value,
          fgCasingPrefix: this.filterForm.get('fgCasingPrefix')?.value,
          fgCasingSuffix: this.filterForm.get('fgCasingSuffix')?.value,
          fgCapCasingPrefix: this.filterForm.get('fgCapPrefix')?.value,
          fgCapCasingSuffix: this.filterForm.get('fgCapSuffix')?.value,
          casingGradeSpecType: 'DEFAULT',
          casingTableFk: null,
          casingGradeFk: ''
        }
      ];
    }
    const payload = {
      code: this.filterForm.get('casingGradeTableCode')?.value,
      description: this.filterForm.get('casingGradeTableDesc')?.value,
      casingGradeSpecificationDTOS: this.buildCasingGradeTablePayload(
        this.casingGradeTablePk,
        this.defaultCasingGradeSpecs,
        [...this.locallyAddedCasingSpec, ...this.specificCasingGradeSpecs],
        this.filterForm
      )
    };
    this.productDataService.createCasingGradeTable(payload).subscribe({
      next: (response) => {
        this.casingGradeTablePk = response.data?.casingGradeTables.casingGradeTablePk;
        this.mode = 'edit';
        this.isSaving = false;
        this.isSaved = true;
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText'),
          timestamp: 3000
        } as Alert);

        this.locallyAddedCasingSpec = [];
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.filterForm.markAsPristine();
        this.displayedRowsForm.markAsPristine();

        // Update baselines after successful save
        this.originalDefaultSpec = {
          defaultCasingGrade: this.filterForm.get('defaultCasingGrade')?.value || '',
          rmCasingPrefix: this.filterForm.get('rmCasingPrefix')?.value || '',
          rmCasingSuffix: this.filterForm.get('rmCasingSuffix')?.value || '',
          fgCasingSuffix: this.filterForm.get('fgCasingSuffix')?.value || '',
          fgCapPrefix: this.filterForm.get('fgCapPrefix')?.value || '',
          fgCasingPrefix: this.filterForm.get('fgCasingPrefix')?.value || '',
          fgCapSuffix: this.filterForm.get('fgCapSuffix')?.value || ''
        };
        this.originalSpecificSpecs = structuredClone(this.specificCasingGradeSpecs);
        this.defaultModifiedValues = null;
        this.modifiedSpecificRows = {};
        // NEW: clear removed specs after successful save
        this.removedSpecificSpecIds = [];

        if (this.mode !== 'edit') {
          this.router.navigate(['/home/productData/casing-grade-table', this.casingGradeTablePk]);
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isSaving = false;
        this.alertService.showAlert({
          type: AlertType.critical,
          content: error?.error?.message || this.translationService.translate('createErrorText'),
          timestamp: 5000
        } as Alert);
        this.cdr.markForCheck();
      }
    });
  }

  private updateCasingGradeTable(): void {
    const completePayload = this.buildCasingGradeTablePayload(
      this.casingGradeTablePk,
      this.defaultCasingGradeSpecs,
      [...this.locallyAddedCasingSpec, ...this.specificCasingGradeSpecs],
      this.filterForm
    );
    this.productDataService.updateCasingGradeTable(this.casingGradeTablePk, completePayload).subscribe({
      next: () => {
        this.isSaving = false;
        this.isSaved = true;
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText'),
          timestamp: 3000
        } as Alert);

        this.locallyAddedCasingSpec = [];
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.filterForm.markAsPristine();
        this.displayedRowsForm.markAsPristine();

        // Update baselines after successful save
        this.originalDefaultSpec = {
          defaultCasingGrade: this.filterForm.get('defaultCasingGrade')?.value || '',
          rmCasingPrefix: this.filterForm.get('rmCasingPrefix')?.value || '',
          rmCasingSuffix: this.filterForm.get('rmCasingSuffix')?.value || '',
          fgCasingSuffix: this.filterForm.get('fgCasingSuffix')?.value || '',
          fgCapPrefix: this.filterForm.get('fgCapPrefix')?.value || '',
          fgCasingPrefix: this.filterForm.get('fgCasingPrefix')?.value || '',
          fgCapSuffix: this.filterForm.get('fgCapSuffix')?.value || ''
        };
        this.originalSpecificSpecs = structuredClone(this.specificCasingGradeSpecs);
        this.defaultModifiedValues = null;
        this.modifiedSpecificRows = {};
        // NEW: clear removed specs after successful save
        this.removedSpecificSpecIds = [];

        if (this.mode !== 'edit') {
          this.router.navigate(['/home/productData/casing-grade-table', this.casingGradeTablePk]);
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isSaving = false;
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('saveErrorText'),
          timestamp: 5000
        } as Alert);
        console.error('Error saving casing grade specifications:', error);
        this.cdr.markForCheck();
      }
    });
  }

  checkIfCasingGradeCodeExists() {
    if (this.mode === 'edit') return; // Skip existence check in edit mode
    const codeControl = this.filterForm.get('casingGradeTableCode');
    const casingGradeCode = codeControl?.value;
    if (!codeControl) return;
    if (!casingGradeCode || (typeof casingGradeCode === 'string' && casingGradeCode.trim() === '')) {
      codeControl.updateValueAndValidity();
      return;
    }
    this.productDataService.casingGradeCodeExists(casingGradeCode).subscribe((exists) => {
      const currentErrors = { ...(codeControl.errors || {}) };
      if (exists) {
        currentErrors.exists = true;
        codeControl.setErrors(currentErrors);
      } else {
        if (currentErrors.exists) delete currentErrors.exists;
        if (Object.keys(currentErrors).length === 0) {
          codeControl.setErrors(null);
          codeControl.updateValueAndValidity();
        } else {
          codeControl.setErrors(currentErrors);
        }
      }
    });
  }

  private buildCasingGradeTablePayload(
    casingGradeTablePk: string,
    defaultSpecs: CasingGradeSpecification[],
    specificSpecs: CasingGradeSpecification[],
    filterForm: FormGroup
  ): CasingGradeSpecification[] {
    const defaultPayload = defaultSpecs.map((spec) => ({
      ...spec,
      casingGrade: filterForm.get('defaultCasingGrade')?.value || spec.casingGrade,
      rmCasingPrefix: filterForm.get('rmCasingPrefix')?.value || spec.rmCasingPrefix,
      rmCasingSuffix: filterForm.get('rmCasingSuffix')?.value || spec.rmCasingSuffix,
      fgCasingPrefix: filterForm.get('fgCasingPrefix')?.value || spec.fgCasingPrefix,
      fgCasingSuffix: filterForm.get('fgCasingSuffix')?.value || spec.fgCasingSuffix,
      fgCapCasingPrefix: filterForm.get('fgCapPrefix')?.value || spec.fgCapCasingPrefix,
      fgCapCasingSuffix: filterForm.get('fgCapSuffix')?.value || spec.fgCapCasingSuffix,
      casingGradeSpecType: 'DEFAULT',
      casingTableFk: casingGradeTablePk
    }));
    const specificPayload = specificSpecs.map((spec) => ({
      ...spec,
      casingGradeSpecType: 'SPECIFIC',
      casingTableFk: casingGradeTablePk
    }));
    return [...defaultPayload, ...specificPayload];
  }

  checkIfModified(): boolean {
    // NEW: deletions in copy mode count as modifications
    if (this.removedSpecificSpecIds.length > 0) return true;
    if (this.checkIfDefaultModified()) return true;
    if (this.locallyAddedCasingSpec.length > 0) return true;

    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const locallyAddedCount = this.locallyAddedCasingSpec.length;
    for (let i = 0; i < this.originalSpecificSpecs.length; i++) {
      const formIndex = i + locallyAddedCount;
      if (formIndex >= formArray.length) continue;
      const original = this.originalSpecificSpecs[i];
      const current = formArray.at(formIndex)?.value;
      if (!current || !original) continue;
      const fields = [
        'casingGradeTier',
        'casingGradeNtr',
        'casingGradeAge',
        'casingGradeAxle',
        'casingGradeCasingSize',
        'casingGradeNail',
        'casingGradeRnailCrown',
        'casingGradeRnailShoulder',
        'casingGradeSr',
        'casingGradeMaxRepairs',
        'casingGrade',
        'rmCasingPrefix',
        'rmCasingSuffix',
        'fgCasingPrefix',
        'fgCasingSuffix',
        'fgCapCasingPrefix',
        'fgCapCasingSuffix'
      ];
      const modified = fields.some((f) => (current[f] ?? '') !== (original[f] ?? ''));
      if (modified) return true;
    }
    return false;
  }

  private checkIfDefaultModified(): boolean {
    const currentDefaultValues = {
      defaultCasingGrade: this.filterForm.get('defaultCasingGrade')?.value || '',
      rmCasingPrefix: this.filterForm.get('rmCasingPrefix')?.value || '',
      rmCasingSuffix: this.filterForm.get('rmCasingSuffix')?.value || '',
      fgCasingSuffix: this.filterForm.get('fgCasingSuffix')?.value || '',
      fgCapPrefix: this.filterForm.get('fgCapPrefix')?.value || '',
      fgCasingPrefix: this.filterForm.get('fgCasingPrefix')?.value || '',
      fgCapSuffix: this.filterForm.get('fgCapSuffix')?.value || ''
    };
    return Object.keys(currentDefaultValues).some((k) => currentDefaultValues[k] !== (this.originalDefaultSpec[k] ?? ''));
  }

  onCancel(): void {
    this.router.navigate(['/home/productData/casing-grade-table/'], { relativeTo: this.route });
  }

  generateFilterFormBackend(): void {
    if (!this.filterFormBackend) this.filterFormBackend = this.fb.group({});
    this.filterFormBackend.patchValue({
      casingGradeTableCode: this.filterForm.get('casingGradeTableCode')?.value || '',
      casingGradeTableDesc: this.filterForm.get('casingGradeTableDesc')?.value || '',
      defaultCasingGrade: this.filterForm.get('defaultCasingGrade')?.value || ''
    });
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.isSaved = false;
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isSaved = false;
    });
  }

  private syncFormToDataModel(): void {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray) return;
    const locallyAddedCopy = JSON.parse(JSON.stringify(this.locallyAddedCasingSpec));
    const specificSpecsCopy = JSON.parse(JSON.stringify(this.specificCasingGradeSpecs));
    const combined = [...locallyAddedCopy, ...specificSpecsCopy];
    for (let i = 0; i < formArray.length; i++) {
      const fg = formArray.at(i) as FormGroup;
      if (fg && combined[i]) Object.assign(combined[i], fg.value);
    }
    this.locallyAddedCasingSpec = combined.slice(0, this.locallyAddedCasingSpec.length);
    this.specificCasingGradeSpecs = combined.slice(this.locallyAddedCasingSpec.length);
  }

  onEnforceThreeDigits(event: any): void {
    CasingGradeConstants.enforceThreeDigits(event);
  }

  get canEditCasingGrade(): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return roles.includes('SETUP_CASING_GRADE_EDIT') && !this.productSaveDisabled;
  }

  private disableAllFormControls(): void {
    const defaultFields = [
      'defaultCasingGrade',
      'defaultRmCasingPrefix',
      'defaultRmCasingSuffix',
      'defaultFgCasingPrefix',
      'defaultFgCasingSuffix',
      'defaultFgCapPrefix',
      'defaultFgCapSuffix'
    ];
    defaultFields.forEach((f) => this.filterForm.get(f)?.disable());
    const rowsArray = this.displayedRowsForm.get('rows') as FormArray;
    rowsArray.controls.forEach((c) => {
      if (c instanceof FormGroup) {
        Object.values(c.controls).forEach((ctrl) => ctrl.disable());
      }
    });
  }

  private beforeUnloadHandler = (event: BeforeUnloadEvent): void => {
    if (this.checkIfModified()) {
      event.returnValue = this.translationService.translate('discardChangesText');
    }
  };

  // NEW: capture unsaved modifications prior to backend reload
  private captureUnsavedModifications(): void {
    // Default spec
    this.defaultModifiedValues = this.checkIfDefaultModified()
      ? {
          defaultCasingGrade: this.filterForm.get('defaultCasingGrade')?.value || '',
          rmCasingPrefix: this.filterForm.get('rmCasingPrefix')?.value || '',
          rmCasingSuffix: this.filterForm.get('rmCasingSuffix')?.value || '',
          fgCasingSuffix: this.filterForm.get('fgCasingSuffix')?.value || '',
          fgCapPrefix: this.filterForm.get('fgCapPrefix')?.value || '',
          fgCasingPrefix: this.filterForm.get('fgCasingPrefix')?.value || '',
          fgCapSuffix: this.filterForm.get('fgCapSuffix')?.value || ''
        }
      : null;

    // Specific specs
    this.syncFormToDataModel();
    this.modifiedSpecificRows = {};
    const fields = [
      'casingGradeTier',
      'casingGradeNtr',
      'casingGradeAge',
      'casingGradeAxle',
      'casingGradeCasingSize',
      'casingGradeNail',
      'casingGradeRnailCrown',
      'casingGradeRnailShoulder',
      'casingGradeSr',
      'casingGradeMaxRepairs',
      'casingGrade',
      'rmCasingPrefix',
      'rmCasingSuffix',
      'fgCasingPrefix',
      'fgCasingSuffix',
      'fgCapCasingPrefix',
      'fgCapCasingSuffix'
    ];
    this.specificCasingGradeSpecs.forEach((spec) => {
      const original = this.originalSpecificSpecs.find((o) => o.casingGradeSpecPk === spec.casingGradeSpecPk);
      if (!original) return;
      const changed: any = {};
      let hasChange = false;
      fields.forEach((f) => {
        if ((spec as any)[f] !== (original as any)[f]) {
          changed[f] = (spec as any)[f];
          hasChange = true;
        }
      });
      if (hasChange) {
        this.modifiedSpecificRows[spec.casingGradeSpecPk] = changed;
      }
    });
  }

  // NEW: reapply unsaved specific row modifications after backend fetch
  private applySpecificRowModifications(): void {
    if (!this.modifiedSpecificRows) return;
    this.specificCasingGradeSpecs.forEach((spec) => {
      const mod = this.modifiedSpecificRows[spec.casingGradeSpecPk];
      if (mod) Object.assign(spec, mod);
    });
  }
}
